/**
 * Whitelist of safe href schemes for user-rendered links.
 *
 * Allows only `http(s)://` URLs and app-relative paths starting with `/`,
 * blocking `javascript:`, `data:`, and similar XSS vectors.
 *
 * @param href - The href value to validate.
 * @returns `true` if the href is safe to render in an `<a href>` attribute.
 */
export const isSafeHref = (href: string): boolean =>
  /^https?:\/\//.test(href) || href.startsWith("/");

/**
 * Removes a single query parameter from the address bar without navigating.
 *
 * Used for params that are consumed once on arrival (a login email, an
 * affiliate code) and should not linger where they can be re-shared or picked
 * up by analytics. Goes through `replaceState` rather than the router,
 * because no route reads these params.
 *
 * @param name - The query parameter to remove.
 * @returns `true` if the parameter was present and removed.
 */
export const stripSearchParam = (name: string): boolean => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(name)) return false;

    url.searchParams.delete(name);
    // Carry the existing state through: TanStack Router keeps its own key and
    // index in history.state, and blanking those desyncs back/forward.
    window.history.replaceState(
      window.history.state,
      document.title,
      url.toString(),
    );

    return true;
  } catch {
    // history API unavailable -- the param simply stays in the URL
    return false;
  }
};

/**
 * Constrains a stored return path to a same-origin, app-relative location.
 *
 * Stricter than {@link isSafeHref}, which also permits absolute `http(s)` URLs:
 * a return path is replayed by the router after login, so it must never be able
 * to leave the app. Values come out of localStorage, which the app cannot treat
 * as its own input.
 *
 * Rejected: absolute URLs, protocol-relative `//host` and its `/\host`
 * backslash variant (browsers normalise `\` to `/` in authority position), and
 * anything not anchored at `/`.
 *
 * @param path - The candidate path, typically read from storage.
 * @returns The path if it is a safe same-origin target, otherwise `null`.
 */
export const toSafeReturnPath = (path: string): string | null => {
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//") || path.startsWith("/\\")) return null;

  return path;
};
